/** Bulk actions for tasks selected by long-press (phone) or Ctrl/⌘-click (desktop). */
import { BellRing, CalendarDays, Check, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { todayInTz } from '@karya/core';
import { db } from '@/db/dexie';
import * as m from '@/data/mutations';
import { useTimeSettings } from '@/data/queries';
import { useUI } from '@/state/ui';
import { cn } from '@/lib/cn';

export function SelectionBar() {
  const selection = useUI((s) => s.selection);
  const setSelection = useUI((s) => s.setSelection);
  const { tz } = useTimeSettings();
  if (selection.length === 0) return null;
  const run = async (label: string, fn: (id: string) => Promise<unknown>) => {
    const ids = [...selection];
    setSelection([]);
    for (const id of ids) await fn(id);
    toast(`${label}: ${ids.length} task${ids.length === 1 ? '' : 's'}`);
  };
  const btn =
    'flex flex-1 flex-col items-center gap-0.5 rounded-sm px-2 py-1.5 text-[11px] font-medium hover:bg-white/10';
  return (
    <div
      role="toolbar"
      aria-label="Selected tasks"
      className="safe-bottom fixed inset-x-3 bottom-3 z-40 mx-auto flex max-w-md items-center gap-1 rounded-lg bg-ink p-1.5 text-bg shadow-modal md:bottom-6"
    >
      <span className="px-2 text-sm font-medium tabular whitespace-nowrap">
        {selection.length} selected
      </span>
      <button
        className={btn}
        onClick={() =>
          void run('Completed', async (id) => {
            const t = await db().tasks.get(id);
            if (t) await m.completeTask(t.id);
          })
        }
      >
        <Check className="size-4" />
        Complete
      </button>
      <button
        className={btn}
        onClick={() => void run('Snoozed 1 hour', (id) => m.snoozeTask(id, '1h'))}
      >
        <BellRing className="size-4" />
        Snooze
      </button>
      <button
        className={btn}
        onClick={() =>
          void run('Moved to tomorrow', (id) =>
            m.updateTask(id, { due_date: m.quickDate('tomorrow', todayInTz(tz)) }),
          )
        }
      >
        <CalendarDays className="size-4" />
        Tomorrow
      </button>
      <button
        className={cn(btn, 'text-[#ffb4ae]')}
        onClick={() => void run('Moved to Trash', (id) => m.deleteTask(id))}
      >
        <Trash2 className="size-4" />
        Delete
      </button>
      <button
        aria-label="Clear selection"
        className="rounded-sm p-2 hover:bg-white/10"
        onClick={() => setSelection([])}
      >
        <X className="size-4" />
      </button>
    </div>
  );
}

/** Toggle a task in the current selection. */
export function toggleSelected(id: string): void {
  const { selection, setSelection } = useUI.getState();
  setSelection(selection.includes(id) ? selection.filter((x) => x !== id) : [...selection, id]);
}
